// Client helpers for the general-purpose Asset search/attach/generate/list/
// delete endpoints (server/routes/assets.js). Two independent, both-optional
// scopes (at least one required per asset): fileId/beatId (a document-local
// attachment -- the short-form editor's Beat Annotations panel) and
// projectId/entityId (a project-wide consistency reference for a character/
// location/style/prop, linked to a Story Ledger entity -- the Project Asset
// Library page). REST-backed (not IndexedDB/offline-sync) -- same reasoning
// as useStoryboardShots.js: these are server-side search/storage
// operations, not offline-editable document content.
import { api } from '../../../services/apiClient';

// Raw candidates from genery.io/eyecandy.com for a search query, not yet
// re-hosted -- { genery: [{imageUrl, notes}], eyecandy: [{imageUrl, notes}] }.
export async function searchAssetCandidates(query) {
  return api.post('/api/assets/search', { query });
}

// Re-hosts the selected candidate and records it as an Asset row. Returns
// the created asset, including a resolved `imageUrl`.
export async function attachAsset({ fileId = null, beatId = null, projectId = null, entityId = null, isCanonical = false, label, kind, selectedImageUrl, sourceNotes }) {
  return api.post('/api/assets/attach', { fileId, beatId, projectId, entityId, isCanonical, label, kind, selectedImageUrl, sourceNotes });
}

// Uploads one or more real user-picked files (a multi-select or whole-folder
// browser pick, not a search result) -- each becomes its own Asset row,
// labeled with its own filename (server/routes/assets.js). Never set
// Content-Type manually for FormData -- the browser must set the multipart
// boundary itself.
export async function uploadAssets({ files, fileId = null, beatId = null, projectId = null, entityId = null, isCanonical = false, kind, sourceNotes }) {
  const formData = new FormData();
  Array.from(files).forEach((file) => formData.append('files', file));
  if (fileId) formData.append('fileId', fileId);
  if (beatId) formData.append('beatId', beatId);
  if (projectId) formData.append('projectId', projectId);
  if (entityId) formData.append('entityId', entityId);
  if (isCanonical) formData.append('isCanonical', 'true');
  formData.append('kind', kind);
  if (sourceNotes) formData.append('sourceNotes', sourceNotes);
  return api.post('/api/assets/upload', formData);
}

// Clones a character's voice from a real recorded/uploaded audio sample and
// saves it as a `voice-ref` Asset (kind: 'voice-ref', source: 'recorded') --
// entityId is required (unlike every other asset kind here, a voice always
// belongs to one character). Never set Content-Type manually for FormData --
// same rule uploadAssets follows above.
export async function cloneVoiceAsset({ projectId, entityId, label, speechNotes = '', isCanonical = true, file }) {
  const formData = new FormData();
  formData.append('projectId', projectId);
  formData.append('entityId', entityId);
  formData.append('label', label);
  if (speechNotes) formData.append('speechNotes', speechNotes);
  formData.append('isCanonical', String(isCanonical));
  formData.append('file', file);
  return api.post('/api/assets/voice/clone', formData);
}

// Creates an Asset directly from an ALREADY-hosted URL -- no search/upload
// step needed (unlike attachAsset/uploadAssets, this never re-hosts
// anything). Used for a 'picked' voice-ref asset: an existing
// ElevenLabs account voice's own preview_url is already a real, permanent
// URL, so there's nothing to download/re-upload.
export async function createAsset({ fileId = null, beatId = null, projectId = null, entityId = null, isCanonical = false, imageUrl, label, kind, source = 'search_reference', sourceNotes }) {
  return api.post('/api/assets', { fileId, beatId, projectId, entityId, isCanonical, imageUrl, label, kind, source, sourceNotes });
}

// Updates an existing asset's sourceNotes (e.g. a voice-ref's {voiceId,
// speechNotes} JSON), isCanonical flag, and/or entityConfirmed flag (confirms
// a pending auto-detected entity tag) -- at least one required.
export async function updateAsset(assetId, { isCanonical, sourceNotes, entityConfirmed } = {}) {
  const body = {};
  if (isCanonical !== undefined) body.isCanonical = isCanonical;
  if (sourceNotes !== undefined) body.sourceNotes = sourceNotes;
  if (entityConfirmed !== undefined) body.entityConfirmed = entityConfirmed;
  return api.patch(`/api/assets/${assetId}`, body);
}

// Confirms a pending auto-detected entity tag (entityConfirmed: false ->
// true) -- the "checkmark" action on a pending sticky-note chip.
export async function confirmAssetEntity(assetId) {
  return api.patch(`/api/assets/${assetId}`, { entityConfirmed: true });
}

// Adds a SECOND (or third, ...) entity tag to an existing image -- creates a
// new Asset row that reuses the existing asset's own raw imageKey server-side
// (see server/routes/assets.js's POST /:id/tag doc comment: never a signed/
// resolved imageUrl), under a different entityId. The "+" affordance on an
// Asset Library card.
export async function tagAssetWithEntity(assetId, entityId) {
  return api.post(`/api/assets/${assetId}/tag`, { entityId });
}

// Lists assets matching any combination of fileId/beatId/projectId/entityId/
// kind/isCanonical -- each row already has a resolved `imageUrl`.
export async function listAssets(params) {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') query.set(key, String(value));
  });
  return api.get(`/api/assets?${query.toString()}`);
}

export async function setAssetCanonical(assetId, isCanonical = true) {
  return api.patch(`/api/assets/${assetId}`, { isCanonical });
}

export async function deleteAsset(assetId) {
  return api.delete(`/api/assets/${assetId}`);
}

// Every character/location/prop entity known anywhere in a project (not
// scoped to one file) -- used by the Asset Library's entity-linking picker.
export async function listProjectEntities(projectId, entityType = null) {
  const query = entityType ? `?entityType=${encodeURIComponent(entityType)}` : '';
  return api.get(`/api/story-ledger/project/${projectId}${query}`);
}
